import {ChangeDetectionStrategy, Component, computed, DestroyRef, Injectable, input, Input, signal} from "@angular/core";
import {AgendaelementId, AgendaelementModell, MøteId, MøteModell, TilgangsrolleMedlem} from "felles-ng2/tjenester/ressurser/generertNg2HttpKlient";
import {initDebugger} from "felles-ng2/støttefunksjoner/debug";
import {Observable} from "rxjs";
import {takeUntilDestroyed} from "@angular/core/rxjs-interop";
import {NyeSakerSkjemaKontroller} from "../nye-saker-skjema/nye-saker-skjema";
import {SakslisteKomponent} from "../saksliste/saksliste";
import {SakDatatjeneste} from "felles-ng2/tjenester/datalager/SakDatatjeneste";
import {l} from "types/typescriptdefinisjoner_lederplan";
import {kopierFraAjsTilNg2} from "felles-ng2/modell/ajsNg2ModellBroer";
import {erNil} from "felles-ng2/støttefunksjoner/nilsjekk";
import {BrukerkontekstTilbyder} from "felles-ng2/tjenester/BrukerkontekstTilbyder";
import {Navigasjon} from "web-ng2/tjenester/Navigasjon";
import {ValgtSakRegister} from "web-ng2/tjenester/ValgtSakRegister";
import {ValgtDokumentRegister} from "web-ng2/moduler/sak/tjenester/ValgtDokumentRegister";

const debug = initDebugger('SakslisteAjsKomponent');

/**
 * Saksliste for bruk i ajs
 */
@Component({
    selector: 'saksliste-ajs',
    templateUrl: 'saksliste-ajs.html',
    styleUrl: 'saksliste-ajs.scss',
    imports: [
        NyeSakerSkjemaKontroller,
        SakslisteKomponent
    ],
    providers: [ValgtSakRegister],
    standalone: true,
    changeDetection: ChangeDetectionStrategy.OnPush
})
export class SakslisteAjsKomponent {
    static ajsSelector = 'sakslisteAjs';

    /** Slik at omverden kan aktivere sorteringsmoduset */
    aktiverRedigerRekkefoelge = input(false);
    
    moete = signal<MøteModell>(null as any); //as any fordi vi ellers få typefeil, vet at møte vil være ikke-null nå moete aksesseres
    saker = signal<AgendaelementModell[]>([]);
    sorteringAktivert = signal(false);
    nyeSakerSkjemaDeaktivert = computed(()=> this.erNyeSakerSkjemaDeaktivert());

    constructor(private avhengigheter: SakslisteAjsKomponentAvhengigheter,
                private kontekst:BrukerkontekstTilbyder,
                private navigasjon:Navigasjon,
                private destroyRef:DestroyRef) {
    }

    @Input({required: true, alias: 'moete'}) set setMøte(møte: l.MøteModell) {
        if (erNil(møte)) return;
        this.moete.set(kopierFraAjsTilNg2(møte));
        this.avhengigheter.hentSaker(this.moete().id)
          .pipe(takeUntilDestroyed(this.destroyRef))
          .subscribe(saker => this.saker.set(saker))
    }

    /** Forhindrer at ny-sak inputfeltet vises når saksrekkefølge er redigert eller dersom bruker ikke har tilgang */
    erNyeSakerSkjemaDeaktivert() {
        return this.sorteringAktivert()
          || erNil(this.moete())
          || this.kontekst.autentisertBruker().ikkeInneharTilgangsrolleIGruppe(TilgangsrolleMedlem.skrive, this.moete().eiendeGruppeId)
          || this.moete().detteEllerPaafoelgendeMoeteLaast;
    }

    vedSorteringAktivert(aktivert:boolean) {
        this.sorteringAktivert.set(aktivert);
        if (aktivert) this.navigasjon.gåTilMøte(this.moete().id);
    }

    vedVisFulltNyeSakerSkjema(viser:boolean) {
        //i tilfelle skjema ble aktivert mens bruker var på sak-detaljer siden
        //sørger vi for å vise sakslisten i full bredde ved å nav til møte
        if (viser) this.navigasjon.gåTilMøte(this.moete().id);
    }

    vedNyeSakerOpprettet(nyeSakerId: AgendaelementId[]) {
        //kaller Async-versjonen fordi NavigasjonDeaktivering.reaktiverNavigasjon gjør kall mot ajs pakket inn i ajsRootScope.$evalAsync(..)
        //dermed er navigasjon fortsatt forhindret når vedNyeSakerOpprettet kalles
        this.navigasjon.gåTilMøteOgSakAsync(this.moete().id, nyeSakerId[0]);
    }
}

@Injectable()
export class SakslisteAjsKomponentAvhengigheter {

    constructor(private sakDatatjeneste: SakDatatjeneste) {
    }

    hentSaker(møteId: MøteId): Observable<AgendaelementModell[]> {
        return this.sakDatatjeneste.finnTilknyttetMøteFraLokallager(møteId) as Observable<AgendaelementModell[]>;
    }

}
